import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  fetchTask,
  updateTask,
  deleteTask,
  fetchTasks,
} from "../redux/actions/taskActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { fetchFiles } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import CustomDatePicker from "../components/CustomDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/esm/locale/en-GB";
import { nl } from "date-fns/locale";
import {
  Container,
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { textFieldStyles } from "../styles/textFieldStyles";

import { useLocation } from "react-router-dom";
import BreadcrumbNav from "../components/BreadcrumbNav";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};



const ViewEditTaskPage = () => {
  const { t, i18n } = useTranslation(['pages', 'forms', 'buttons', 'common']);

  const taskPriorities = [
    { value: "Low", label: t('pages:tasks.priority_options.low') },
    { value: "Medium", label: t('pages:tasks.priority_options.medium') },
    { value: "High", label: t('pages:tasks.priority_options.high') }
  ];

  const taskColours = [
    { name: "Red", color: "#FF0000", label: t('pages:tasks.colours.red') },
    { name: "Yellow", color: "#FFFF00", label: t('pages:tasks.colours.yellow') },
    { name: "Green", color: "#008000", label: t('pages:tasks.colours.green') },
    { name: "Blue", color: "#0000FF", label: t('pages:tasks.colours.blue') },
    { name: "No colour", color: "#FFFFFF", label: t('pages:tasks.colours.no_colour') },
  ];

  const taskTypes = [
    { value: "Synchronization with Outlook", label: t('pages:tasks.types.synchronization_outlook') },
    { value: "Task in the procedure", label: t('pages:tasks.types.task_procedure') },
    { value: "Other task", label: t('pages:tasks.types.other_task') },
    { value: "Task directed towards the client", label: t('pages:tasks.types.task_client') },
    { value: "Phone note (lawyer) opposing party", label: t('pages:tasks.types.phone_note_lawyer_opposing') },
    { value: "Phone note client", label: t('pages:tasks.types.phone_note_client') },
    { value: "Phone note third party", label: t('pages:tasks.types.phone_note_third_party') },
  ];

  const taskStatuses = [
    { value: "Not started", label: t('pages:tasks.status_options.not_started') },
    { value: "In progress", label: t('pages:tasks.status_options.in_progress') },
    { value: "Completed", label: t('pages:tasks.status_options.completed') },
    { value: "Pending", label: t('pages:tasks.status_options.pending') },
    { value: "Deferred", label: t('pages:tasks.status_options.deferred') },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const { id } = useParams();
  const { task } = useSelector((state) => state.taskDetails);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded);

  const [owner, setOwner] = useState("");
  const [assignee, setAssignee] = useState("");
  const [type, setType] = useState("");
  const [caseId, setCaseId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [reminder, setReminder] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [colour, setColour] = useState("");

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('common:home'), href: "/" },
    { label: t('pages:tasks.title'), href: "/tasks" },
  ]);

  const locales = {
    "en-GB": enGB,
    "nl": nl,
  };

  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    const initialCaseFile =
      files?.find((file) => file._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t('common:case_file'),
          href: `/files/${initialCaseFile._id}/tasks`,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchTask(id));
    dispatch(fetchRelationships());
    dispatch(fetchFiles());
  }, [dispatch, id]);

  useEffect(() => {
    if (task) {
      console.log("task is ", task);
      setOwner(task.owner ? task.owner._id : "");
      setAssignee(task.assignee ? task.assignee._id : "");
      setType(task.type || "");
      setCaseId(task.case ? task.case._id : "");
      setSubject(task.subject || "");
      setDescription(task.description || "");
      
      // Convert the date string to a Date object for DateTimePicker
      if (task.dueDate) {
        const dateObject = new Date(task.dueDate);
        setDueDate(dateObject);
      } else {
        setDueDate(null);
      }
      
      setReminder(task.reminder || "");
      setStatus(task.status || "");
      setPriority(task.priority || "");
      setColour(task.colour || "");
    }
  }, [task, files, relationships]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedTask = {
      _id: id,
      owner,
      ...(assignee !== undefined && { assignee: assignee || null }),
      type,
      case: caseId,
      subject,
      description,
      dueDate, // This will still be in YYYY-MM-DD format as needed by the API
      reminder,
      status,
      priority,
      colour,
    };
    dispatch(updateTask(updatedTask));
    dispatch(fetchFiles());
    dispatch(fetchTasks());
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const handleDelete = () => {
    dispatch(deleteTask(id));
    navigate("/tasks");
  };

  const handleCancel = () => {
    navigate("/tasks");
  };

  const inputStyles = {
    width: "30%",
    marginBottom: 2,
  };

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
       <BreadcrumbNav
        title={t('pages:tasks.edit_title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <FormControl variant="standard" sx={{ mr: 3, width: "30%" }}>
              <CustomAutocomplete
                options={relationships}
                value={relationships?.find((rel) => rel._id === owner) || null}
                onChange={(event, newValue) =>
                  setOwner(newValue ? newValue._id : "")
                }
                label={t('forms:owner')}
                filterType="Member"
                addNewLabel={t('common:add_owner')}
                addNewPath="/relationships/create"
              />
            </FormControl>
            {(user?.role === "Manager") && (
              <FormControl variant="standard" sx={{ mr: 3, width: "30%" }}>
                <CustomAutocomplete
                  options={relationships}
                  value={relationships?.find((rel) => rel._id === assignee) || null}
                  onChange={(event, newValue) =>
                    setAssignee(newValue ? newValue._id : "")
                  }
                  label={t('forms:labels.assignedTo')}
                  filterType="Member"
                  addNewLabel={t('common:add_owner')}
                  addNewPath="/relationships/create"
                />
              </FormControl>
            )}
            <Autocomplete
              options={taskTypes}
              getOptionLabel={(option) => option.label}
              value={taskTypes.find(t => t.value === type) || null}
              size="small"
              onChange={(event, newValue) => setType(newValue ? newValue.value : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('common:type')}
                  variant="outlined"
                  required
                />
              )}
              sx={textFieldStyles({ width: "30%" })}
            />
            <FormControl variant="outlined" sx={{ width: "30%" }}>
              <FileAutocomplete
                options={files}
                value={files?.find((file) => file._id === caseId) || null}
                onChange={(event, newValue) =>
                  setCaseId(newValue ? newValue._id : "")
                }
                label={t('common:case')}
                name="case"
              />
            </FormControl>
            <TextField
              label={t('forms:subject')}
              variant="outlined"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={textFieldStyles({ width: "63.5%" })}
              size="small"
            />
            <TextField
              label={t('forms:labels.description')}
              variant="outlined"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles({ width: "63.5%" })}
              size="small"
            />
            <Box sx={{ width: "30%" }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'nl' ? nl : enGB}>
                <DateTimePicker
                  value={dueDate}
                  onChange={(newValue) => setDueDate(newValue)}
                  inputFormat="dd/MM/yyyy HH:mm"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: textFieldStyles({ width: "100%" }),
                      size: "small",
                      label: t('common:due_date'),
                    },
                  }}
                  minutesStep={15}
                  ampm={false}
                />
              </LocalizationProvider>
            </Box>
            <TextField
              label={t('forms:placeholders.reminder_days_before')}
              variant="outlined"
              type="number"
              fullWidth
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              sx={textFieldStyles({ width: "30%" })}
              size="small"
            />
            <FormControl
              variant="outlined"
              sx={textFieldStyles({ width: "30%" })}
              size="small"
              required
            >
              <InputLabel id="status-select-label">{t('common:status')}</InputLabel>
              <Select
                labelId="status-select-label"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label={t('common:status')}
              >
                {taskStatuses.map((taskStatus) => (
                  <MenuItem key={taskStatus.value} value={taskStatus.value}>
                    {taskStatus.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              sx={textFieldStyles({ width: "30%" })}
              size="small"
            >
              <InputLabel id="priority-select-label">{t('common:priority')}</InputLabel>
              <Select
                labelId="priority-select-label"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                label={t('common:priority')}
              >
                {taskPriorities.map((taskPriority) => (
                  <MenuItem key={taskPriority.value} value={taskPriority.value}>
                    {taskPriority.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              sx={textFieldStyles({ width: "30%" })}
              size="small"
            >
              <InputLabel id="colour-select-label">{t('forms:labels.colour')}</InputLabel>
              <Select
                labelId="colour-select-label"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                label={t('forms:colour')}
              >
                {taskColours.map((taskColour) => (
                  <MenuItem key={taskColour.name} value={taskColour.name}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: taskColour.color,
                          marginRight: 1,
                        }}
                      />
                      {taskColour.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              // padding: "10px 16px",
              // background: "#fff",
              zIndex: 10,
              backgroundColor: "white",
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: "18px",
              fontWeight: 500,
              color: "#000000",
              padding: "13px 80px",
            }}
          >
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              size="small"
            >
              {t('buttons:secondary.cancel')}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} size="small">
              {t('buttons:primary.save_task')}
            </SubmitButton>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default ViewEditTaskPage;
